import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { AsyncPipe, DatePipe, JsonPipe } from '@angular/common';

import { BehaviorSubject, catchError, combineLatest, finalize, map, Observable, of, shareReplay, Subject, switchMap, tap } from 'rxjs';

import { CardModule } from 'primeng/card';
import { RadioButtonModule } from 'primeng/radiobutton';
import { InputTextModule } from 'primeng/inputtext';
import { FloatLabel } from 'primeng/floatlabel';
import { Button } from 'primeng/button';
import { Table, TableModule } from 'primeng/table';
import { InputNumber } from 'primeng/inputnumber';
import { DatePicker } from 'primeng/datepicker';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { MultiSelectModule } from 'primeng/multiselect';

import { LogService } from 'src/app/services/log.service';
import { SearchFilter } from 'src/app/models/search-filter';
import { AuthService } from 'src/app/services/auth.service';

/**
 * Contains tools for developers
 */
@Component({
	selector: 'app-dev-tools',
	templateUrl: './dev-tools.component.html',
	styleUrl: './dev-tools.component.scss',
	imports: [
		CardModule,
		RadioButtonModule,
		ReactiveFormsModule,
		InputTextModule,
		FloatLabel,
		Button,
		TableModule,
		AsyncPipe,
		DatePipe,
		JsonPipe,
		InputNumber,
		DatePicker,
		TooltipModule,
		MultiSelectModule,
	],
	providers: [LogService],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DevToolsComponent implements OnInit {
	public searchToolTip = 'New logs may take a few minutes to appear in CloudWatch Logs Insights searches.';

	private localStorageLogGroupsKey = 'selectedLogGroups';

	public selectedLogGroupsForm = new FormControl<string[]>([]);

	/**
	 * Used by primeng to determine which table rows that are expanded
	 */
	public expandedRows = {};

	/**
	 * Enables search when input id is given
	 */
	public searchDisabled: boolean = true;

	/**
	 * For max date in calendar selection
	 * @returns current date
	 */
	public maxDate(): Date {
		return new Date();
	}

	/**
	 * Shows loading icon when searching
	 */
	public searchLoading: boolean = false;

	private searchId: Subject<string> = new Subject<string>();

	private searchFilter: BehaviorSubject<SearchFilter | undefined> = new BehaviorSubject<SearchFilter | undefined>(undefined);

	/**
	 * For tracking the input search id. Updated when clicking search
	 */
	public searchId$: Observable<string> = this.searchId.asObservable();

	/**
	 * For tracking search filter. Updated when clicking search
	 */
	public searchFilter$: Observable<SearchFilter | undefined> = this.searchFilter.asObservable();

	/**
	 * Clears the resulting logs table filters
	 * @param table
	 */
	public clearFilters(table: Table) {
		table.clear();
	}

	public updateSavedSelection() {
		const selectedLogGroups = this.selectedLogGroupsForm.value ?? [];
		this.searchDisabled = selectedLogGroups.length === 0;
		localStorage.setItem(this.localStorageLogGroupsKey, JSON.stringify(selectedLogGroups));
	}

	/**
	 * Types available to search for. Used for identificaiton and display name
	 */
	public types: QueryType[] = [
		{ Name: 'Session Id', Id: 'sessionId' },
		{ Name: 'Correlation Id', Id: 'correlationId' },
	];

	/**
	 * Form group for handling both search input and search filter
	 */
	public logsQueryForm = new FormGroup({
		selectedType: new FormControl<QueryType>(this.types[0]),
		inputId: new FormControl<string | null>(null),
		lookbackTime: new FormControl<number | null>(15),
		endTime: new FormControl<Date | null>(null),
		limit: new FormControl<number | null>(1000),
	});

	/**
	 * Trigger log search. This updates input id and search filter which will trigger the search.
	 */
	public searchLogs() {
		this.searchDisabled = true;
		this.searchLoading = true;
		this.searchId.next(this.logsQueryForm.value.inputId ?? '');
		this.searchFilter.next({
			limit: this.logsQueryForm.value.limit ?? 1000,
			endTime: this.logsQueryForm.value.endTime ?? new Date(),
			lookbackTimeInMinutes: this.logsQueryForm.value.lookbackTime ?? 60,
		});
	}

	/**
	 * Response from log query
	 */
	public logs$: Observable<any[] | undefined> = combineLatest({ searchId: this.searchId$, searchFilter: this.searchFilter$ }).pipe(
		switchMap(input => {
			if (!input.searchFilter) return of(undefined);
			if (this.logsQueryForm.value.selectedType?.Id === 'sessionId') {
				return this.logService.logsBySessionId$(input.searchId, this.selectedLogGroupsForm.value, input.searchFilter).pipe(
					tap(() => {
						this.searchDisabled = false;
						this.searchLoading = false;
					}),
					// Serialize to string to make searchable in table
					map(logs => {
						if (logs) {
							return logs?.map(log => {
								return { ...log, RawMessage: JSON.stringify(log.Message) };
							});
						}
						return undefined;
					}),
				);
			} else if (this.logsQueryForm.value.selectedType?.Id === 'correlationId') {
				return this.logService.logsByCorrelationId$(input.searchId, this.selectedLogGroupsForm.value, input.searchFilter).pipe(
					// Serialize to string to make searchable in table
					tap(() => {
						this.searchDisabled = false;
						this.searchLoading = false;
					}),
					map(logs => {
						if (logs) {
							return logs?.map(log => {
								return { ...log, RawMessage: JSON.stringify(log.Message) };
							});
						}
						return undefined;
					}),
				);
			} else {
				return of(undefined);
			}
		}),
		catchError(error => {
			this.messageService.add({ severity: 'error', sticky: false, life: 3000, summary: error?.message ?? 'Failed to query for logs' });
			return of(undefined);
		}),
		finalize(() => {
			this.searchDisabled = false;
			this.searchLoading = false;
		}),
		shareReplay(1),
	);

	/**
	 * Toggles search button and loading icon
	 */
	ngOnInit(): void {
		this.logsQueryForm.valueChanges
			.pipe(
				tap(form => {
					this.searchLoading = false;
					if (form.inputId?.length === 0) {
						this.searchDisabled = true;
					} else {
						this.searchDisabled = false;
					}
				}),
			)
			.subscribe();

		this.authService.userData$
			.pipe(
				tap(userData => {
					if (!this.logsQueryForm.controls.inputId.value) {
						this.logsQueryForm.controls.inputId.setValue(userData.sessionId);
					}
				}),
			)
			.subscribe();

		const storedLogGroupsSelection = JSON.parse(localStorage.getItem(this.localStorageLogGroupsKey) ?? '[]');
		this.selectedLogGroupsForm.patchValue(storedLogGroupsSelection);
		this.searchDisabled = storedLogGroupsSelection.length === 0;
	}

	constructor(
		public logService: LogService,
		private authService: AuthService,
		private messageService: MessageService,
	) {}
}

/**
 * Model for type of query
 */
export interface QueryType {
	Name: string;
	Id: string;
}
